import {DATA_TYPES, HEADER_NAME} from '../../constants/constants'
import {
    CompletionCallMetadata,
    getNextIdForPrefix,
    getSectionNameByPointer,
    ignoredTemplateOutlineTypes,
    invalidOutlineRetryAmount,
    type PointerPredicate,
    reportBIError,
    unsectionedOutlineSectionName
} from './aiExtensionContent'
import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {Pointer} from '@wix/document-services-types'
import type {StructureExtensionAPI} from '../structure'
import type {ComponentDefinitionExtensionAPI} from '../componentDefinition'
import type {SchemaExtensionAPI} from '../schema/schema'
import {getReportableFromError, ReportableError} from '@wix/document-manager-utils'
import {fetchBuilderNewOutlineFromPromptHub} from './aiExtensionUtils'
import {
    aiBuilderInjectionResultConsecutiveErrorMessage,
    aiBuilderInjectionResultConsecutiveErrorType,
    aiBuilderInjectionErrorMessage,
    aiBuilderInjectionErrorType,
    aiContentBadJsonErrorMessage,
    aiContentBadJsonErrorType,
    aiContentUnexpectedError,
    aiContentUnexpectedErrorMessage
} from './constants'
import {type IdMap, injectData, predicateBuilderComp} from './aiBuilderUtils'
import type {BuilderComponentsExtensionAPI} from '../builderComponents/builderComponents'
import {builderTypes} from '../../utils/builderUtils'

interface ValidationResult {
    isValid: boolean
    reason?: string
    value?: string
}

export interface BuilderOutline {
    [section: string]: {[componentName: string]: BuilderComponentData}
}

export interface BuilderComponentData {
    value: string
    metaData: any
    length: number
    prop: string
}

export interface BuilderOutlineWithIdMapAndTargets {
    outline: BuilderOutline
    idMap: IdMap
    targets: string[]
}

export type BuilderAiInjectionResult = Record<string, string>

export interface BuilderAiInjectionResultWithIdMap {
    builderAiInjectionResult: BuilderAiInjectionResult
    idMap: IdMap
}

const validateBuilderInjectionSuggestion = (
    builderAiInjectionResult: BuilderAiInjectionResult,
    targets: string[],
    idMap: IdMap,
    createExtArgs: CreateExtArgs
): ValidationResult => {
    const {components} = createExtArgs.extensionAPI as StructureExtensionAPI
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI

    for (const [componentId, data] of Object.entries(builderAiInjectionResult)) {
        if (!targets.includes(componentId))
            return {
                isValid: false,
                reason: 'result contains component that is not specific in targets',
                value: componentId
            }
        const {pointer: compPointer} = idMap[componentId]
        const componentType = components.getComponentType(compPointer)
        const {prop, path} = idMap[componentId]
        const isInnerElement = path !== undefined
        const dataType = builderTypes(schemaAPI, isInnerElement ? path.join('|') : componentType).data
        const dataToUpdate = schemaAPI.createItemAccordingToSchema(dataType, DATA_TYPES.data, {
            data: {
                [prop]: data
            }
        })
        try {
            schemaAPI.validate(dataType, dataToUpdate, DATA_TYPES.data)
        } catch (e) {
            return {
                isValid: false,
                reason: 'result contains data that does not match the schema',
                value: `${componentType}.Data`
            }
        }
    }

    return {isValid: true}
}

export const applyBuilderInjection = (
    createExtArgs: CreateExtArgs,
    {idMap, builderAiInjectionResult}: BuilderAiInjectionResultWithIdMap
) => {
    Object.entries(builderAiInjectionResult).forEach(([componentId, newText]) => {
        const {pointer, prop, path} = idMap[componentId]
        if (!pointer) return
        const dataToUpdate = () => {
            return {
                data: {
                    [prop]: newText
                }
            }
        }
        injectData(createExtArgs, pointer, path, dataToUpdate)
    })
}

export const getPageOutlineAndTargetsForBuilder = (
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer
): BuilderOutlineWithIdMapAndTargets => {
    const {pointers} = createExtArgs
    const {components} = createExtArgs.extensionAPI as StructureExtensionAPI
    const {componentDefinition} = createExtArgs.extensionAPI as ComponentDefinitionExtensionAPI
    const {builderComponents} = createExtArgs.extensionAPI as BuilderComponentsExtensionAPI

    const getContentRecursively = (
        compPointer: Pointer,
        outline: BuilderOutline,
        typeCounter: Record<string, number>,
        idMap: IdMap,
        outlineSectionName: string | undefined,
        pointerPredicate: PointerPredicate,
        selectedComponent: Pointer,
        targets: string[],
        addToTargets: boolean = false
    ): void => {
        if (compPointer.id === selectedComponent.id) addToTargets = true
        const type = components.getComponentType(compPointer)

        if (ignoredTemplateOutlineTypes.has(type)) {
            return
        }

        if (componentDefinition.isContainer(type)) {
            const containerName =
                componentDefinition.isSection(type) &&
                getSectionNameByPointer(createExtArgs.extensionAPI, compPointer, DATA_TYPES.naming)
            const containerOutlineSectionName = containerName
                ? getNextIdForPrefix(containerName, typeCounter)
                : outlineSectionName

            const children = pointers.structure.getChildren(compPointer)
            for (const childPointer of children) {
                getContentRecursively(
                    childPointer,
                    outline,
                    typeCounter,
                    idMap,
                    containerOutlineSectionName,
                    pointerPredicate,
                    selectedComponent,
                    targets,
                    addToTargets
                )
            }
        }

        if (pointerPredicate && !pointerPredicate(compPointer)) return

        const propsContext = builderComponents.getData(compPointer, true, ['text'])
        propsContext.forEach(context => {
            const {prop, path, value: data, metaData} = context
            const mappedId = getNextIdForPrefix('comp', typeCounter)
            idMap[mappedId] = {pointer: compPointer, prop, path}
            const outlineSectionNameOrDefault =
                outlineSectionName ??
                (pointers.structure.isInMasterPage(compPointer) ? HEADER_NAME : unsectionedOutlineSectionName)
            outline[outlineSectionNameOrDefault] ??= {}
            outline[outlineSectionNameOrDefault][mappedId] = {value: data, length: data?.length, metaData, prop}
            if (addToTargets) targets.push(mappedId)
        })
    }

    const outline: BuilderOutline = {}
    const typeCounter: Record<string, number> = {}
    const idMap = {}

    const predicate = (pointer: Pointer): boolean => {
        const componentType = components.getComponentType(pointer)
        return predicateBuilderComp(createExtArgs.extensionAPI, componentType)
    }

    const pagePointer = pointers.structure.getPageOfComponent(componentPointer)
    const targets: string[] = []
    getContentRecursively(pagePointer, outline, typeCounter, idMap, undefined, predicate, componentPointer, targets)
    return {outline, idMap, targets}
}

export const getComponentInjectionForBuilder = async (
    environmentContext: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer,
    siteType: string,
    businessName: string,
    siteDescription: string,
    userRequest: string
): Promise<BuilderAiInjectionResultWithIdMap> => {
    const {serverFacade} = environmentContext
    const {coreConfig} = createExtArgs
    const {logger} = coreConfig

    const {outline, idMap, targets} = getPageOutlineAndTargetsForBuilder(createExtArgs, componentPointer)
    let builderAiInjectionResult: BuilderAiInjectionResult | null = null

    let apiCallCount = 0
    let lastError: Error | null = null
    const callsMetadata: CompletionCallMetadata[] = []
    let validationResults: ValidationResult

    try {
        while (apiCallCount <= invalidOutlineRetryAmount) {
            const completionResponse = await fetchBuilderNewOutlineFromPromptHub(
                serverFacade,
                userRequest,
                outline,
                targets,
                siteType,
                businessName,
                siteDescription
            )

            const completionText = completionResponse.generatedTexts[0]
            apiCallCount += 1

            try {
                builderAiInjectionResult = JSON.parse(completionText!)
            } catch (e) {
                callsMetadata.push({
                    isBadJson: true
                })
                lastError = new ReportableError({
                    errorType: aiContentBadJsonErrorType,
                    message: aiContentBadJsonErrorMessage,
                    extras: {
                        completionText
                    }
                })
                continue
            }
            validationResults = validateBuilderInjectionSuggestion(
                builderAiInjectionResult!,
                targets,
                idMap,
                createExtArgs
            )
            if (validationResults.isValid) {
                break
            } else if (apiCallCount > 1) {
                reportBIError(
                    logger,
                    aiBuilderInjectionResultConsecutiveErrorType,
                    aiBuilderInjectionResultConsecutiveErrorMessage,
                    builderAiInjectionResult!,
                    validationResults
                )
            } else {
                reportBIError(
                    logger,
                    aiBuilderInjectionErrorType,
                    aiBuilderInjectionErrorMessage,
                    builderAiInjectionResult!,
                    validationResults
                )
            }
        }
        if (!builderAiInjectionResult) {
            throw (
                lastError ??
                new ReportableError({
                    errorType: aiContentUnexpectedError,
                    message: aiContentUnexpectedErrorMessage
                })
            )
        }
        return {builderAiInjectionResult, idMap}
    } catch (e: any) {
        // ensure that tokenUsage is always in the error
        const reportedError = getReportableFromError(e, {
            errorType: e.errorType ?? aiContentUnexpectedError,
            tags: {
                aiContentInjection: true
            }
        })
        logger.captureError(reportedError)

        throw reportedError
    }
}
